<template>
  <div class="white_background full-height">
    <div class="wrapped-page-container chapter-studio ">
      <div class="chapter-studio__title">
        {{ chapterStudioPageConstant.PAGE_TITLE }}
      </div>
      <div v-if="selectedChapter && chapterList && chapterList.length">
        <v-menu :disabled="chapterList.length === 1" class="chapter-studio__chapter-selector" offset-y>
          <v-flex slot="activator" mb-1>
            <span class="pr-3 ypo_blue_color">{{ selectedChapter.name }}</span>
            <v-icon v-if="chapterList.length > 1" class="ypo_blue_color" size="24">arrow_drop_down</v-icon>
          </v-flex>
          <v-list>
            <v-list-tile v-for="chapter in chapterStudioChapterList" :key="chapter.id"
              @click="setSelectedChapter(chapter)">
              {{ chapter.name }}
            </v-list-tile>
          </v-list>
        </v-menu>
      </div>
      <div v-if="warningAnnouncements.length">
        <GeneralAnnouncement :announcements="warningAnnouncements" :is-warning-announcement="true" class="mt-3">
        </GeneralAnnouncement>
      </div>
      <div v-if="generalAnnouncements.length">
        <GeneralAnnouncement :announcements="generalAnnouncements" class="mt-3"></GeneralAnnouncement>
      </div>
      <div>
        <image-announcement-carousel v-if="imageAnnouncements.length" :imageAnnouncements="imageAnnouncements" />
      </div>
      <div class="relative-position full-height">
        <status-overlay :action="loadingResourcesAction" content-height="100px" class="mt-4"></status-overlay>
        <div v-if="chapterResources" class="resources">
          <v-layout v-if="favoriteResources.length" column class="full-width">
            <v-flex class="resources__category-title" pb-2>{{ chapterStudioPageConstant.TITLE_FAVORITE }}</v-flex>
            <v-layout row wrap>
              <v-flex v-for="resource in sortedByTitle(favoriteResources)" :key="`favorite-${resource.resourceId}`"
                class="resource-option">
                <chapter-studio-resource :chapter-resource="resource" :handle-favorite-click="handleFavoriteSelection"
                  :is-favorite="true"></chapter-studio-resource>
              </v-flex>
            </v-layout>
          </v-layout>
          <v-layout v-for="(category, index) in chapterResources" :key="`${index}-${category.categoryName}`"
            class="full-width" column pt-4 wrap>
            <v-flex class="resources__category-title" pb-2>{{ category.categoryName }}</v-flex>
            <v-layout v-if="category.resourceItems && category.resourceItems.length" row class="full-width" wrap>
              <v-flex v-for="resource in sortedByTitle(category.resourceItems)" :key="resource.resourceId"
                class="resource-option">
                <chapter-studio-resource :chapter-resource="resource" :handle-favorite-click="handleFavoriteSelection"
                  :is-favorite="isResourceFavorite(resource.resourceId)"></chapter-studio-resource>
              </v-flex>
            </v-layout>
          </v-layout>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { storeGetters } from "@/store/storeGetters.constant";
import { chapterStudioPageConstant } from "@/components/ChapterStudioPage/chapterStudioPage.constant";
import { storeAction } from "@/store/storeActions.constant";
import ChapterStudioResource from "@/components/ChapterStudioPage/ChapterStudioResource.vue";
import sortBy from "lodash/sortBy";
import { storeMutation } from "@/store/storeMutations.constant";
import { gtmEventName } from "@/plugins/gtmAnalytics";
import { gaDataLayerEvent } from "../../plugins/analytics";
import GeneralAnnouncement from "@/components/ChapterStudioPage/GeneralAnnouncement.vue";
import StatusOverlay from "@/components/StatusOverlay/StatusOverlay.vue";
import ImageAnnouncementCard from "@/components/ChapterStudioPage/ImageAnnouncementCard.vue";
import ImageAnnouncementCarousel from '@/components/ChapterStudioPage/ImageAnnouncementCarousel.vue';
import { trackPageLoadTime } from '../../common/utils/performance';

export default {
  name: 'ChapterStudio',
  data() {
    return {
      chapterStudioPageConstant,
      selectedChapter: null,
      closedAnnouncementIds: [],
    };
  },
  components: {
    StatusOverlay,
    GeneralAnnouncement,
    ChapterStudioResource,
    ImageAnnouncementCard,
    ImageAnnouncementCarousel,
  },
  props: {},
  mounted() {
        this.$nextTick(() => {
            trackPageLoadTime('ChapterStudio');
        });
  },
  async created() {
    if (this.selectedChapterFromStore) {
      this.selectedChapter = this.selectedChapterFromStore;
    } else if (this.chapterStudioChapterList && this.chapterStudioChapterList.length) {
      // eslint-disable-next-line prefer-destructuring
      this.selectedChapter = this.chapterStudioChapterList[0];
    }

    if (this.selectedChapter && this.selectedChapter.parentId) {
      await this.getChapterStudioAnnouncements(this.selectedChapter.parentId);
      await this.getChapterStudioResources(this.selectedChapter.id);
    }
  },
  methods: {
    ...mapActions({
      getChapterStudioResources: `chapterStudio/${storeAction.getChapterStudioResources}`,
      addFavoriteToChapterStudio: `chapterStudio/${storeAction.addFavoriteToChapterStudio}`,
      getChapterStudioAnnouncements: `chapterStudio/${storeAction.getChapterStudioAnnouncements}`,
    }),
    ...mapMutations({
      setChapterStudioFavorite: `chapterStudio/${storeMutation.setChapterStudioFavorite}`,
      setChapterStudioSelectedChapter: `chapterStudio/${storeMutation.setChapterStudioSelectedChapter}`,
    }),
    async setSelectedChapter(chapter) {
      if (this.selectedChapter && chapter && chapter.id !== this.selectedChapter.id) {
        this.selectedChapter = chapter;
        this.setChapterStudioSelectedChapter(chapter);
        await this.getChapterStudioResources(chapter.id);
      }
    },
    imageAnnouncementArray(array, size) {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
          result.push(array.slice(i, i + size));
        }
        return result;
    },
    handleFavoriteSelection(resource, isFavorite) {
      this.setChapterStudioFavorite(resource);
      if (isFavorite) {
        gaDataLayerEvent(gtmEventName.C_Chapter_Studio_Favorites_Click, resource.title);
      }
      this.addFavoriteToChapterStudio({
        chapterStudioResourceId: resource.resourceId,
        communityId: this.selectedChapter ? this.selectedChapter.id : "",
        isFavorite,
      });
    },
    isResourceFavorite(resourceId) {
      return this.favoriteResourceIds.includes(resourceId);
    },
    sortedByTitle(resourceList) {
      if (resourceList.length) {
        return sortBy(resourceList, 'title');
      }
    },
  },
  computed: {
    ...mapState('actionState', ['actions']),
    ...mapGetters({
      chapterStudioChapterList: `chapterStudio/${storeGetters.chapterStudioChapterList}`,
      chapterStudioResources: `chapterStudio/${storeGetters.chapterStudioResources}`,
      chapterStudioFavorites: `chapterStudio/${storeGetters.chapterStudioFavorites}`,
      chapterStudioFavoriteResourceIds: `chapterStudio/${storeGetters.chapterStudioFavoriteResourceIds}`,
      selectedChapterFromStore: `chapterStudio/${storeGetters.chapterStudioSelectedChapter}`,
      chapterStudioAnnouncements: `chapterStudio/${storeGetters.chapterStudioAnnouncements}`,
    }),
    loadingResourcesAction() {
      return this.actions[storeAction.getChapterStudioResources];
    },
    chapterList() {
      return this.chapterStudioChapterList;
    },
    totalCards() {
      return this.imageAnnouncements.length;
    },
    favoriteResources() {
      return this.chapterStudioFavorites || [];
    },
    favoriteResourceIds() {
      return this.chapterStudioFavoriteResourceIds || [];
    },
    chapterResources() {
      return this.chapterStudioResources && this.chapterStudioResources.length ? this.chapterStudioResources : null;
    },
    warningAnnouncements() {
      return this.chapterStudioAnnouncements && this.chapterStudioAnnouncements.warning
          ? this.chapterStudioAnnouncements.warning
          : [];
    },
    generalAnnouncements() {
      return this.chapterStudioAnnouncements && this.chapterStudioAnnouncements.general
          ? this.chapterStudioAnnouncements.general
          : [];
    },
    imageAnnouncements() {
      return this.chapterStudioAnnouncements && this.chapterStudioAnnouncements.image
          ? this.chapterStudioAnnouncements.image
          : [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../common/styles/_colors.scss";
@import "../../common/styles/_responsive.scss";
@import "../../common/styles/_commonClasses.scss";

.chapter-studio {
  font-family: Roboto, 'sans-serif';
  max-width: 100%;

  @include breakpointMin(sm) {
    max-width: 800px;
  }

  @include breakpointMin(md) {
    max-width: 1221px;
  }

  &__title {
    font-weight: 600;
    line-height: 42.19px;
    color: $ypo-blue;
    padding-top: 15px;
    font-size: 28px;

    @include breakpointMin(sm) {
      font-size: 30px;
      padding-top: 25px;
    }

    @include breakpointMin(md) {
      font-size: 36px;
    }
  }

  &__chapter-selector {
    font-weight: 400;
    line-height: 32.81px;
    text-align: center;
    padding-left: 3px;
    font-size: 20px;
    padding-top: 8px;

    @include breakpointMin(sm) {
      font-size: 22px;
      padding-top: 15px;
    }

    @include breakpointMin(md) {
      font-size: 26px;
    }
  }

  .resources {
    padding: 15px 0;
    margin: 0;

    &__category-title {
      font-size: 18px;
      font-weight: 400;

      @include breakpointMin(sm) {
        font-size: 22px;
      }
    }

    .resource-option {
      margin: 0 0 20px 0;
      width: 100%;
      flex: 0 1 auto;

      @include breakpointMin(sm) {
        width: calc(100% / 2 - 10px);
        margin: 0 20px 20px 0;
        &:nth-child(even) {
          margin-right: 0;
        }
      }

      @include breakpointMin(md) {
        width: calc(100% / 3 - 15px);
        margin: 0 20px 20px 0;
        &:nth-child(even) {
          margin-right: 20px;
        }
        &:nth-child(3n) {
          margin-right: 0;
        }
      }
    }
  }
}
</style>

